import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/modules/auth/actions';

interface AccessDeniedProps {
  history?: any;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ history }) => {
  const dispatch = useDispatch();

  const [fade, setFade] = useState(true);

  const handleGoHome = () => {
    if (history) {
      history.push('/');
    } else {
      window.location.href = '/';
    }
  };

  useEffect(() => {
    setFade(true);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      dispatch(actions.loginFailure());
    }, 30000);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2c5530 0%, #1e3a8a 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Montserrat', 'Roboto', sans-serif",
        padding: { xs: '20px', md: '40px' },
      }}
    >
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.15; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: { xs: '200px', md: '300px' },
            height: { xs: '200px', md: '300px' },
            borderRadius: '50%',
            background: '#16a34a',
            opacity: 0.1,
            top: '-100px',
            right: '-100px',
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '0s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: { xs: '150px', md: '200px' },
            height: { xs: '150px', md: '200px' },
            borderRadius: '50%',
            background: '#1e40af',
            opacity: 0.1,
            bottom: '-75px',
            left: '-75px',
            animation: 'float 6s ease-in-out infinite',
            animationDelay: '2s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: '#059669',
            opacity: 0.1,
            top: '20%',
            left: '10%',
            animation: 'pulse 4s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: '#3b82f6',
            opacity: 0.1,
            bottom: '30%',
            right: '15%',
            animation: 'pulse 5s ease-in-out infinite',
            animationDelay: '3s',
          }}
        />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          maxWidth: '800px',
          opacity: fade ? 1 : 0,
          animation: 'fadeIn 0.8s ease-out',
        }}
      >
        {/* Main Title */}
        <Typography
          sx={{
            fontSize: { xs: '60px', sm: '80px', md: '100px' },
            background:
              'linear-gradient(135deg, #ffffff 0%, #fca5a5 50%, #ffffff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: 'Caveat, cursive',
            fontWeight: 'bold',
            lineHeight: 1,
            textShadow: '0 0 30px rgba(252, 165, 165, 0.5)',
            filter: 'drop-shadow(2px 4px 6px rgba(0,0,0,0.4))',
            letterSpacing: '-2px',
            transform: 'rotate(-3deg)',
            marginBottom: { xs: '20px', md: '30px' },
          }}
        >
          Acesso Negado
        </Typography>

        {/* Info Card */}
        <Box
          sx={{
            background: 'rgba(100, 130, 180, 0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '16px',
            padding: { xs: '30px 25px', md: '40px 50px' },
            color: 'white',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            marginBottom: { xs: '30px', md: '40px' },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '20px', md: '24px' },
              marginBottom: '15px',
              fontFamily: 'Roboto-Bold',
              color: '#ffffff',
            }}
          >
            🔒 Você não tem permissão para acessar esta página
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '16px', md: '18px' },
              fontFamily: 'Roboto-Regular',
              color: '#ffffff',
              opacity: 0.9,
              lineHeight: 1.6,
            }}
          >
            Seu usuário não possui as permissões necessárias para visualizar
            este conteúdo.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: '15px', md: '20px' },
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            onClick={handleGoHome}
            sx={{
              background: 'linear-gradient(135deg, #16a34a, #10b981)',
              color: 'white',
              padding: { xs: '12px 30px', md: '15px 40px' },
              fontSize: { xs: '16px', md: '18px' },
              fontFamily: 'Roboto-Bold',
              borderRadius: '50px',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(22, 163, 74, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #15803d, #059669)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(22, 163, 74, 0.5)',
              },
            }}
          >
            🏠 Voltar para Login
          </Button>
          {/* <Button
            variant="contained"
            onClick={() => {
              window.location.href = '/solicitar-acesso';
            }}
            sx={{
              background: 'linear-gradient(135deg, #16a34a, #10b981)',
              color: 'white',
              padding: { xs: '12px 30px', md: '15px 40px' },
              fontSize: { xs: '16px', md: '18px' },
              fontFamily: 'Roboto-Bold',
              borderRadius: '50px',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(22, 163, 74, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #15803d, #059669)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(22, 163, 74, 0.5)',
              },
            }}
          >
            📡 Solicitar Acesso
          </Button> */}
        </Box>
      </Box>

      {/* DTI Badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: '20px', md: '40px' },
          right: { xs: '20px', md: '40px' },
          minWidth: '160px',
          background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 8px 20px rgba(30, 64, 175, 0.3)',
          zIndex: 15,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '12px', md: '14px' },
            opacity: 0.95,
            marginBottom: '4px',
            lineHeight: 1.3,
            fontFamily: 'Roboto-Regular',
          }}
        >
          Diretoria de
          <br />
          Tecnologia e Inovação
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', md: '18px' },
            letterSpacing: '2px',
            fontFamily: 'Roboto-Bold',
          }}
        >
          DTI
        </Typography>
      </Box>
    </Box>
  );
};

export default AccessDenied;
